import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  doc, 
  deleteDoc,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { StaffMember } from '../types';
import { getCachedStaff, dataCache } from '../services/dataCacheService';
import { useAcademicYear } from '../context/AcademicYearContext';
import * as XLSX from 'xlsx';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Loader2,
  UserCheck,
  Search,
  UserPlus,
  IdCard,
  Briefcase,
  UserCog,
  Calendar,
  FileDown,
  FileUp,
  Phone,
  Users,
  Calendar as CalendarIcon,
  AlertTriangle,
  RefreshCw,
  Printer,
  FileSpreadsheet,
  UserX,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import BirthDatePicker from './BirthDatePicker';

interface StaffManagementProps {
  category: 'educational' | 'assistant';
}

const StaffManagement: React.FC<StaffManagementProps> = ({ category }) => {
  const { currentYearObj } = useAcademicYear();
  const formRef = useRef<HTMLDivElement>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewingStaff, setViewingStaff] = useState<StaffMember | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ show: boolean; id: string | null; isBulk: boolean }>({
    show: false,
    id: null,
    isBulk: false
  });

  // Filters and Inactive states
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'substitute' | 'inactive'>('all');
  const [substituteFilter, setSubstituteFilter] = useState<'all' | 'permanent' | 'substitute'>('all');
  const [sortField, setSortField] = useState<'fullName' | 'nationalId' | 'role' | 'active' | 'phone' | 'type'>('fullName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isPrintingList, setIsPrintingList] = useState(false);

  const handleSort = (field: 'fullName' | 'nationalId' | 'role' | 'active' | 'phone' | 'type') => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: 'fullName' | 'nationalId' | 'role' | 'active' | 'phone' | 'type') => {
    if (sortField !== field) {
      return <ArrowUpDown size={13} className="text-slate-400 opacity-50 group-hover:opacity-100" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp size={14} className="text-indigo-600 font-bold" />
    ) : (
      <ChevronDown size={14} className="text-indigo-600 font-bold" />
    );
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nationalId: '',
    birthDate: '',
    phone: '',
    relativeName: '',
    relativePhone: '',
    role: '',
    type: category as 'educational' | 'assistant',
    isSubstitute: false,
    active: true,
    schedule: {} as { [day: string]: string[] }
  });
  const [roles, setRoles] = useState<string[]>([
    'מורה',
    'סייע/ת',
    'סייעת',
    'אם בית',
    'מטפל/ת רגשי',
    'קלינאי/ת תקשורת',
    'מרפא/ה בעיסוק',
    'פסיכולוג/ית',
    'יועץ/ת חינוכי/ת',
    'מורה לשילוב',
    'מנהל/ת',
    'מזכיר/ה'
  ]);
  const daysOfWeek = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי'];
  const schoolHours = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  const [activeScheduleDay, setActiveScheduleDay] = useState('ראשון');

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, [category]);

  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchRoles = async () => {
    try {
      const q = query(collection(db, 'staffRoles'), orderBy('name'));
      const snapshot = await getDocs(q);
      const dbRoles = snapshot.docs.map(doc => doc.data().name);
      
      // Combine defaults with DB roles, removing duplicates
      const defaultRoles = [
        'מורה', 'סייע/ת', 'סייעת', 'אחות', 'אם בית', 'מטפל/ת רגשי', 'קלינאי/ת תקשורת', 
        'מרפא/ה בעיסוק', 'פסיכולוג/ית', 'יועץ/ת חינוכי/ת', 
        'מורה לשילוב', 'מנהל/ת', 'מזכיר/ה'
      ];
      
      const combined = Array.from(new Set([...defaultRoles, ...dbRoles])).sort();
      setRoles(combined);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchStaff = async (forceRefresh = false) => {
    let safetyTimer: any = setTimeout(() => {
      setLoading(false);
    }, 5000);
    setLoading(true);
    try {
      const allStaff = await getCachedStaff(forceRefresh);
      const staffData = allStaff
        .filter((s: any) => {
          // Determine effective type for filtering
          const role = (s.role || '').trim();
          const isAssistantRole = role.includes('סייע') || role.includes('אם בית');
          
          let effectiveType = s.type;
          
          // If the role is clearly an assistant role, it MUST be in the assistant category
          // to satisfy the user's request for automatic movement.
          if (isAssistantRole) {
            effectiveType = 'assistant';
          } else if (!effectiveType) {
            effectiveType = 'educational';
          }
          
          return effectiveType === category;
        })
        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '', 'he')) as StaffMember[];
      setStaff(staffData);
      setLastUpdated(new Date());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'staff');
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      setLoading(false);
    }
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.nationalId) return;

    if (formData.nationalId.length !== 9) {
      alert('תעודת זהות חייבת להכיל 9 ספרות');
      return;
    }

    const existingStaff = staff.find(s => s.nationalId === formData.nationalId);
    if (existingStaff) {
      alert(`כבר קיים איש צוות עם תעודת זהות זו: ${existingStaff.fullName}`);
      return;
    }

    const finalType = formData.role === 'אחות' ? 'educational' : formData.type;

    setActionLoading(true);
    try {
      await addDoc(collection(db, 'staff'), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        nationalId: formData.nationalId,
        birthDate: formData.birthDate,
        phone: formData.phone,
        relativeName: formData.relativeName,
        relativePhone: formData.relativePhone,
        role: formData.role,
        isSubstitute: formData.isSubstitute,
        schedule: formData.schedule,
        type: finalType,
        active: true,
        createdAt: new Date().toISOString()
      });
      dataCache.remove('staff'); // Invalidate cache
      resetForm();
      setIsAdding(false);
      fetchStaff(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'staff');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStaff = async (id: string) => {
    if (!formData.firstName || !formData.lastName || !formData.nationalId) return;

    if (formData.nationalId.length !== 9) {
      alert('תעודת זהות חייבת להכיל 9 ספרות');
      return;
    }

    const existingStaff = staff.find(s => s.nationalId === formData.nationalId && s.id !== id);
    if (existingStaff) {
      alert(`כבר קיים איש צוות אחר עם תעודת זהות זו: ${existingStaff.fullName}`);
      return;
    }

    const finalType = formData.role === 'אחות' ? 'educational' : formData.type;

    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'staff', id), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: `${formData.firstName} ${formData.lastName}`,
        nationalId: formData.nationalId,
        birthDate: formData.birthDate,
        phone: formData.phone,
        relativeName: formData.relativeName,
        relativePhone: formData.relativePhone,
        role: formData.role,
        isSubstitute: formData.isSubstitute,
        schedule: formData.schedule,
        type: finalType,
        active: formData.active
      });
      dataCache.remove('staff'); // Invalidate cache
      setEditingId(null);
      resetForm();
      fetchStaff(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'staff');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteStaff = async (id: string) => {
    setConfirmDelete({ show: true, id, isBulk: false });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setConfirmDelete({ show: true, id: null, isBulk: true });
  };

  const executeDelete = async () => {
    setActionLoading(true);
    try {
      if (confirmDelete.isBulk) {
        const promises = selectedIds.map(id => 
          updateDoc(doc(db, 'staff', id), { active: false })
        );
        await Promise.all(promises);
        setSelectedIds([]);
      } else if (confirmDelete.id) {
        await updateDoc(doc(db, 'staff', confirmDelete.id), {
          active: false
        });
      }
      dataCache.remove('staff'); // Invalidate cache
      setConfirmDelete({ show: false, id: null, isBulk: false });
      fetchStaff(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'staff');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredStaff.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredStaff.map(s => s.id));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleStatusChange = async (s: StaffMember, newStatus: 'active' | 'inactive') => {
    const newActive = newStatus === 'active';
    const scrollPos = window.scrollY;
    setActionLoading(true);
    // Optimistic UI update
    setStaff(prev => prev.map(item => item.id === s.id ? { ...item, active: newActive } : item));
    try {
      await updateDoc(doc(db, 'staff', s.id), { active: newActive });
      dataCache.remove('staff');
    } catch (error) {
      console.error('Error updating staff status:', error);
      handleFirestoreError(error, OperationType.UPDATE, 'staff');
      fetchStaff(true);
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  const handleBulkStatusChange = async (targetActive: boolean) => {
    if (selectedIds.length === 0) return;
    const scrollPos = window.scrollY;
    setActionLoading(true);
    try {
      const promises = selectedIds.map(id => updateDoc(doc(db, 'staff', id), { active: targetActive }));
      await Promise.all(promises);
      setStaff(prev => prev.map(item => selectedIds.includes(item.id) ? { ...item, active: targetActive } : item));
      dataCache.remove('staff');
      setSelectedIds([]);
    } catch (error) {
      console.error('Error updating bulk staff status:', error);
      handleFirestoreError(error, OperationType.UPDATE, 'staff');
      fetchStaff(true);
    } finally {
      setActionLoading(false);
      requestAnimationFrame(() => window.scrollTo({ top: scrollPos, behavior: 'instant' }));
    }
  };

  const resetForm = () => {
    setFormData({ 
      firstName: '',
      lastName: '',
      nationalId: '',
      birthDate: '',
      phone: '',
      relativeName: '',
      relativePhone: '',
      role: '', 
      type: category as 'educational' | 'assistant',
      isSubstitute: false,
      active: true,
      schedule: {}
    });
  };

  const downloadTemplate = () => {
    const template = [
      {
        'שם פרטי': 'ישראל',
        'שם משפחה': 'ישראלי',
        'תעודת זהות': '123456789',
        'תאריך לידה': '1990-01-01',
        'טלפון נייד': '0501234567',
        'שם קרוב משפחה': 'שרה ישראלי',
        'טלפון קרוב משפחה': '0507654321',
        'תפקיד': 'מורה'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `staff_template_${category}.xlsx`);
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        // raw: false helps with formatted strings, but we still need to be careful with phone numbers
        const data = XLSX.utils.sheet_to_json(ws, { raw: false, dateNF: 'yyyy-mm-dd' }) as any[];

        setActionLoading(true);
        const skipped = [];
        for (const item of data) {
          const firstName = item['שם פרטי']?.toString().trim() || '';
          const lastName = item['שם משפחה']?.toString().trim() || '';
          const nationalId = item['תעודת זהות']?.toString().trim() || '';
          
          if (nationalId) {
            const existing = staff.find(s => s.nationalId === nationalId);
            if (existing) {
              skipped.push(`${firstName} ${lastName} (${nationalId})`);
              continue;
            }
          }

          // Fix phone number leading zero
          let phone = item['טלפון נייד']?.toString().trim() || '';
          if (phone && /^\d{9}$/.test(phone)) {
            phone = '0' + phone;
          }

          // Fix relative phone leading zero
          let relativePhone = item['טלפון קרוב משפחה']?.toString().trim() || '';
          if (relativePhone && /^\d{9}$/.test(relativePhone)) {
            relativePhone = '0' + relativePhone;
          }

          if (firstName && lastName && nationalId) {
            await addDoc(collection(db, 'staff'), {
              firstName,
              lastName,
              fullName: `${firstName} ${lastName}`,
              nationalId,
              birthDate: item['תאריך לידה']?.toString().trim() || '',
              phone,
              relativeName: item['שם קרוב משפחה']?.toString().trim() || '',
              relativePhone,
              role: item['תפקיד']?.toString().trim() || '',
              isSubstitute: item['מילוי מקום'] === 'כן' || item['מילוי מקום'] === true,
              schedule: {},
              type: category,
              active: true,
              createdAt: new Date().toISOString()
            });
          }
        }
        fetchStaff();
        if (skipped.length > 0) {
          alert(`הייבוא הושלם. דולגו ${skipped.length} רשומות עם תעודת זהות קיימת:\n${skipped.join('\n')}`);
        } else {
          alert('הייבוא הושלם בהצלחה!');
        }
      } catch (error) {
        console.error("Error importing excel:", error);
        alert('שגיאה בייבוא הקובץ. וודא שהפורמט תקין.');
      } finally {
        setActionLoading(false);
        if (e.target) e.target.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const potentialDuplicates = useMemo(() => {
    const duplicates = new Set<string>();
    const names = new Map<string, string[]>();
    const ids = new Map<string, string[]>();

    staff.forEach(s => {
      if (!s.active) return;
      
      const name = s.fullName?.trim();
      const id = s.nationalId?.trim();

      if (name) {
        if (!names.has(name)) names.set(name, []);
        names.get(name)!.push(s.id);
      }
      if (id) {
        if (!ids.has(id)) ids.set(id, []);
        ids.get(id)!.push(s.id);
      }
    });

    names.forEach(idsList => {
      if (idsList.length > 1) idsList.forEach(id => duplicates.add(id));
    });
    ids.forEach(idsList => {
      if (idsList.length > 1) idsList.forEach(id => duplicates.add(id));
    });

    return duplicates;
  }, [staff]);

  const activeStaff = useMemo(() => staff.filter(s => s.active !== false), [staff]);
  const inactiveStaff = useMemo(() => staff.filter(s => s.active === false), [staff]);
  const substituteStaff = useMemo(() => staff.filter(s => s.isSubstitute && s.active !== false), [staff]);

  const filteredStaff = useMemo(() => {
    return staff
      .filter(s => {
        const matchesSearch = (s.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (s.nationalId || '').includes(searchTerm) ||
          (s.phone || '').includes(searchTerm) ||
          (s.role || '').toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = roleFilter === 'all' || s.role === roleFilter;

        const matchesSub = substituteFilter === 'all' 
          ? true 
          : substituteFilter === 'substitute' 
            ? s.isSubstitute 
            : !s.isSubstitute;

        // Active / Inactive status filter
        const isStaffActive = s.active !== false;
        let matchesStatus = true;
        if (statusFilter === 'active') {
          matchesStatus = isStaffActive;
        } else if (statusFilter === 'substitute') {
          matchesStatus = s.isSubstitute && isStaffActive;
        } else if (statusFilter === 'inactive') {
          matchesStatus = !isStaffActive;
        } else {
          // 'all' filter: Inactive staff are hidden by default unless showInactive is checked
          if (!showInactive && !isStaffActive) {
            matchesStatus = false;
          }
        }

        if (showDuplicatesOnly) {
          return matchesSearch && matchesRole && matchesSub && matchesStatus && potentialDuplicates.has(s.id);
        }
        return matchesSearch && matchesRole && matchesSub && matchesStatus;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'fullName':
            comparison = (a.fullName || '').localeCompare(b.fullName || '', 'he');
            break;
          case 'nationalId':
            comparison = (a.nationalId || '').localeCompare(b.nationalId || '');
            break;
          case 'role':
            comparison = (a.role || '').localeCompare(b.role || '', 'he');
            break;
          case 'active': {
            const aAct = a.active !== false ? 1 : 0;
            const bAct = b.active !== false ? 1 : 0;
            comparison = aAct - bAct;
            break;
          }
          case 'phone':
            comparison = (a.phone || '').localeCompare(b.phone || '');
            break;
          case 'type': {
            const aType = a.isSubstitute ? 1 : 0;
            const bType = b.isSubstitute ? 1 : 0;
            comparison = aType - bType;
            break;
          }
          default:
            comparison = (a.fullName || '').localeCompare(b.fullName || '', 'he');
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [staff, searchTerm, showDuplicatesOnly, potentialDuplicates, roleFilter, substituteFilter, statusFilter, showInactive, sortField, sortDirection]);

  const title = category === 'educational' ? 'צוות חינוכי טיפולי' : 'סייעות ומילוי מקום';

  const handlePrintList = () => {
    setIsPrintingList(true);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('נא לאפשר חלונות קופצים כדי להדפיס את הרשימה');
      setIsPrintingList(false);
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
        <head>
          <meta charset="utf-8" />
          <title>רשימת ${title} - ${currentYearObj.name}</title>
          <style>
            body { font-family: system-ui, -apple-system, sans-serif; margin: 20px; direction: rtl; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #4f46e5; padding-bottom: 12px; margin-bottom: 16px; }
            .header h1 { margin: 0; font-size: 22px; color: #1e1b4b; }
            .header p { margin: 4px 0 0 0; color: #64748b; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
            th { background-color: #f1f5f9; color: #1e293b; padding: 8px 10px; border: 1px solid #cbd5e1; text-align: right; }
            td { padding: 7px 10px; border: 1px solid #cbd5e1; text-align: right; }
            tr:nth-child(even) { background-color: #f8fafc; }
            .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px; }
            .badge-active { background: #dcfce7; color: #15803d; }
            .badge-inactive { background: #fee2e2; color: #b91c1c; }
            .badge-sub { background: #fef3c7; color: #92400e; }
            @media print {
              body { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>רשימת ${title}</h1>
              <p>שנת לימודים: ${currentYearObj.name} • תאריך הפקה: ${new Date().toLocaleDateString('he-IL')} • סה״כ מוצגים: ${filteredStaff.length}</p>
            </div>
            <img src="/logo.png" style="height: 48px;" alt="לוגו" onerror="this.style.display='none'" />
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 35px; text-align: center;">#</th>
                <th>שם מלא</th>
                <th>תעודת זהות</th>
                <th>תפקיד</th>
                <th>סטטוס</th>
                <th>סוג</th>
                <th>טלפון</th>
                <th>איש קשר לחירום</th>
              </tr>
            </thead>
            <tbody>
              ${filteredStaff.map((s, idx) => `
                <tr>
                  <td style="text-align: center;">${idx + 1}</td>
                  <td style="font-weight: bold;">${s.firstName} ${s.lastName}</td>
                  <td style="font-family: monospace;">${s.nationalId}</td>
                  <td>${s.role || '-'}</td>
                  <td>
                    <span class="badge ${s.active !== false ? 'badge-active' : 'badge-inactive'}">
                      ${s.active !== false ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </td>
                  <td>
                    <span class="badge ${s.isSubstitute ? 'badge-sub' : 'badge-active'}">
                      ${s.isSubstitute ? 'מילוי מקום' : 'קבוע'}
                    </span>
                  </td>
                  <td>${s.phone || '-'}</td>
                  <td>${s.relativeName ? `${s.relativeName} (${s.relativePhone || ''})` : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => {
              setTimeout(() => { window.print(); }, 400);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setIsPrintingList(false);
  };

  return (
    <div className="space-y-6" dir="rtl">
      <AnimatePresence>
        {viewingStaff && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h3 className="text-xl font-bold text-slate-900">פרטי איש צוות</h3>
                <button 
                  onClick={() => setViewingStaff(null)}
                  className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center">
                    <UserCog size={40} />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-slate-900">{viewingStaff.firstName} {viewingStaff.lastName}</h4>
                    <p className="text-slate-500 font-medium">{viewingStaff.role || 'ללא תפקיד מוגדר'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">תעודת זהות</label>
                      <div className="flex items-center gap-2 text-slate-700 font-mono">
                        <IdCard size={16} className="text-slate-300" />
                        <span>{viewingStaff.nationalId}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">תאריך לידה</label>
                      <div className="flex items-center gap-2 text-slate-700">
                        <Calendar size={16} className="text-slate-300" />
                        <span>{viewingStaff.birthDate || 'לא הוזן'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">טלפון נייד</label>
                      <a 
                        href={`https://wa.me/${viewingStaff.phone?.replace(/^0/, '972')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-indigo-600 font-bold hover:underline"
                      >
                        <Phone size={16} className="text-indigo-300" />
                        <span>{viewingStaff.phone || 'לא הוזן'}</span>
                      </a>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-2">איש קשר לחירום</label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-slate-700 font-bold">
                          <Users size={16} className="text-slate-300" />
                          <span>{viewingStaff.relativeName || 'לא הוזן'}</span>
                        </div>
                        {viewingStaff.relativePhone && (
                          <div className="flex items-center gap-2 text-slate-600 text-sm">
                            <Phone size={14} className="text-slate-300" />
                            <span>{viewingStaff.relativePhone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">סטטוס</label>
                      <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${
                        viewingStaff.isSubstitute ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
                      }`}>
                        {viewingStaff.isSubstitute ? 'מילוי מקום' : 'קבוע'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-slate-100">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-4">מערכת שעות שבועית</label>
                  <div className="grid grid-cols-3 gap-3">
                    {daysOfWeek.map(day => {
                      const hours = viewingStaff.schedule?.[day] || [];
                      return (
                        <div key={day} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <span className="text-xs font-bold text-slate-700 block mb-2">{day}</span>
                          <div className="flex flex-wrap gap-1">
                            {hours.length > 0 ? hours.map(h => (
                              <span key={h} className="w-5 h-5 flex items-center justify-center bg-white text-indigo-600 rounded-md text-[10px] font-bold shadow-sm border border-slate-100">
                                {h}
                              </span>
                            )) : <span className="text-[10px] text-slate-300 italic">אין שעות</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                <button 
                  onClick={() => setViewingStaff(null)}
                  className="px-8 py-3 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-100 transition-all"
                >
                  סגור
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDelete.show && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trash2 size={40} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">אישור מחיקה</h3>
                <p className="text-slate-500 mb-8">
                  {confirmDelete.isBulk 
                    ? `האם אתה בטוח שברצונך למחוק ${selectedIds.length} אנשי צוות?` 
                    : 'האם אתה בטוח שברצונך למחוק איש צוות זה?'}
                  <br />
                  <span className="text-sm text-red-500 mt-2 block font-medium">פעולה זו תהפוך את הרשומות ללא פעילות.</span>
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={executeDelete}
                    disabled={actionLoading}
                    className="flex-1 bg-red-600 text-white font-bold py-4 rounded-2xl hover:bg-red-700 transition-all shadow-lg shadow-red-100 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                    אישור ומחיקה
                  </button>
                  <button
                    onClick={() => setConfirmDelete({ show: false, id: null, isBulk: false })}
                    className="flex-1 bg-slate-100 text-slate-600 font-bold py-4 rounded-2xl hover:bg-slate-200 transition-all"
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">ניהול {title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">הוספה, עריכה וניהול של רשימת ה{category === 'educational' ? 'צוות' : 'סייעות'}</p>
            {lastUpdated && (
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                עודכן לאחרונה: {lastUpdated.toLocaleTimeString('he-IL')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => fetchStaff(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-2.5 rounded-xl font-bold hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50 text-xs"
            title="רענן נתונים מהשרת"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>רענן</span>
          </button>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 px-4 py-2.5 rounded-xl font-bold hover:bg-red-100 transition-all shadow-sm text-xs"
            >
              <Trash2 size={16} />
              <span>מחק מסומנים ({selectedIds.length})</span>
            </button>
          )}
          <button
            onClick={handlePrintList}
            className="inline-flex items-center gap-2 bg-white text-emerald-600 border border-emerald-200 px-4 py-2.5 rounded-xl font-bold hover:bg-emerald-50 transition-all shadow-sm text-xs"
            title="הדפסת רשימת אנשי הצוות או שמירה כ-PDF"
          >
            <Printer size={16} />
            <span>הדפסת רשימה</span>
          </button>
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 bg-white text-emerald-600 border border-emerald-200 px-4 py-2.5 rounded-xl font-bold hover:bg-emerald-50 transition-all shadow-sm text-xs"
          >
            <FileSpreadsheet size={16} />
            <span>דוגמה ל-EXCEL</span>
          </button>
          <label className="inline-flex items-center gap-2 bg-white text-emerald-600 border border-emerald-200 px-4 py-2.5 rounded-xl font-bold hover:bg-emerald-50 transition-all shadow-sm cursor-pointer text-xs">
            <FileUp size={16} />
            <span>ייבוא מ-EXCEL</span>
            <input type="file" accept=".xlsx, .xls" onChange={handleImportExcel} className="hidden" />
          </label>
          <button
            onClick={() => {
              let publicBaseUrl = window.location.origin;
              if (publicBaseUrl.includes('ais-dev-')) {
                publicBaseUrl = publicBaseUrl.replace('ais-dev-', 'ais-pre-');
              }
              const url = `${publicBaseUrl}/?mode=self-entry&type=${category}`;
              navigator.clipboard.writeText(url);
              alert('הקישור הציבורי הועתק ללוח בהצלחה!\nניתן לשלוח אותו כעת לאנשי הצוות (הקישור מוגדר כציבורי ונגיש לכולם).');
            }}
            className="inline-flex items-center gap-2 bg-white text-indigo-600 border border-indigo-200 px-4 py-2.5 rounded-xl font-bold hover:bg-indigo-50 transition-all shadow-sm text-xs"
          >
            <IdCard size={16} />
            <span>קישור להזנה עצמית</span>
          </button>
          <button
            onClick={() => {
              setIsAdding(true);
              setEditingId(null);
              resetForm();
            }}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 text-xs"
          >
            <Plus size={18} />
            <span>הוספת איש צוות חדש</span>
          </button>
        </div>
      </div>

      {/* Academic Year Info Banner & Quick Filter Tabs (Consistent with Student Management) */}
      <div className="bg-gradient-to-r from-indigo-500 via-indigo-600 to-purple-600 rounded-3xl p-6 text-white shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <Calendar size={28} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-indigo-200 font-bold">שנת לימודים פעילה</span>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-black">{currentYearObj.name}</span>
            </div>
            <h3 className="text-xl font-bold mt-0.5">ניהול {title}</h3>
            <p className="text-xs text-indigo-100 mt-1">
              סה״כ {staff.length} במאגר • {activeStaff.length} פעילים • {substituteStaff.length} מילוי מקום {inactiveStaff.length > 0 ? `• ${inactiveStaff.length} לא פעילים (${showInactive ? 'מוצגים כעת' : 'מוסתרים כברירת מחדל'})` : ''}
            </p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex bg-black/20 p-1.5 rounded-2xl backdrop-blur-md text-xs font-bold shrink-0 self-start md:self-auto gap-1">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'all' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            כל המאגר ({staff.length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'active' 
                ? 'bg-white text-indigo-700 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            פעילים ({activeStaff.length})
          </button>
          <button
            onClick={() => setStatusFilter('substitute')}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'substitute' 
                ? 'bg-amber-400 text-amber-950 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            מילוי מקום ({substituteStaff.length})
          </button>
          <button
            onClick={() => {
              setStatusFilter('inactive');
              setShowInactive(true);
            }}
            className={`px-3 py-2 rounded-xl transition-all ${
              statusFilter === 'inactive' 
                ? 'bg-red-400 text-red-950 shadow-sm' 
                : 'text-white/80 hover:text-white'
            }`}
          >
            לא פעילים ({inactiveStaff.length})
          </button>
        </div>
      </div>

      {/* Filter Bar with Search, Dropdowns and Inactive Checkbox */}
      <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row items-center gap-4">
        <div className="flex items-center gap-3 flex-1 w-full">
          <Search className="text-slate-400" size={20} />
          <input
            type="text"
            placeholder="חיפוש לפי שם, תעודת זהות, תפקיד או טלפון..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent text-sm font-medium"
          />
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-52 border-r md:border-r border-slate-100 pr-4">
          <Briefcase className="text-slate-400 shrink-0" size={18} />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="flex-1 outline-none text-slate-700 bg-transparent text-xs font-bold appearance-none cursor-pointer"
          >
            <option value="all">כל התפקידים</option>
            {roles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 w-full md:w-44 border-r md:border-r border-slate-100 pr-4">
          <UserCog className="text-slate-400 shrink-0" size={18} />
          <select
            value={substituteFilter}
            onChange={(e) => setSubstituteFilter(e.target.value as any)}
            className="flex-1 outline-none text-slate-700 bg-transparent text-xs font-bold appearance-none cursor-pointer"
          >
            <option value="all">כל הסוגים</option>
            <option value="permanent">קבוע בלבד</option>
            <option value="substitute">מילוי מקום בלבד</option>
          </select>
        </div>

        <button
          onClick={() => setShowDuplicatesOnly(!showDuplicatesOnly)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border font-bold text-xs transition-all shrink-0 ${
            showDuplicatesOnly 
              ? 'bg-amber-50 border-amber-200 text-amber-700 shadow-inner' 
              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
          title="הצג רק אנשי צוות בעלי שם או תעודת זהות כפולים"
        >
          <AlertTriangle className={`w-4 h-4 ${showDuplicatesOnly ? 'text-amber-500' : 'text-slate-400'}`} />
          <span>{showDuplicatesOnly ? 'הצג הכל' : 'כפילויות'}</span>
          {potentialDuplicates.size > 0 && !showDuplicatesOnly && (
            <span className="bg-amber-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
              {potentialDuplicates.size}
            </span>
          )}
        </button>

        {/* Checkbox: Show / Hide Inactive Staff (default unchecked / hidden) */}
        <div className="flex items-center w-full md:w-auto border-t md:border-t-0 md:border-r border-slate-100 pt-3 md:pt-0 md:pr-4 shrink-0">
          <label className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl border transition-all cursor-pointer select-none ${
            showInactive 
              ? 'bg-red-50 border-red-200 text-red-800 shadow-sm' 
              : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
          }`}>
            <input
              type="checkbox"
              id="toggle-inactive-staff"
              checked={showInactive}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowInactive(checked);
                if (!checked && statusFilter === 'inactive') {
                  setStatusFilter('all');
                }
              }}
              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
            />
            <span className="flex items-center gap-1.5 text-xs font-bold">
              <UserX size={16} className={showInactive ? 'text-red-500' : 'text-slate-400'} />
              <span>הצג צוות לא פעיל</span>
              {inactiveStaff.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  showInactive ? 'bg-red-200 text-red-900' : 'bg-slate-200 text-slate-600'
                }`}>
                  {inactiveStaff.length}
                </span>
              )}
            </span>
          </label>
        </div>
      </div>

      {/* Summary and Active Filters Feedback */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-500 px-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span>
            מוצגים <strong className="text-slate-900 font-bold">{filteredStaff.length}</strong> אנשי צוות
          </span>
          {!showInactive && inactiveStaff.length > 0 && statusFilter !== 'inactive' && (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-slate-200">
              <UserX size={13} className="text-slate-400" />
              <span>{inactiveStaff.length} אנשי צוות לא פעילים מוסתרים (ברירת מחדל)</span>
              <button 
                type="button"
                onClick={() => setShowInactive(true)}
                className="text-indigo-600 hover:text-indigo-800 underline font-bold cursor-pointer mr-1"
                title="סמן ב-V להצגת אנשי צוות לא פעילים"
              >
                הצג אותם
              </button>
            </span>
          )}
          {showInactive && inactiveStaff.length > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-xl text-[11px] font-semibold border border-red-200">
              <UserX size={13} className="text-red-500" />
              <span>כולל {inactiveStaff.length} אנשי צוות לא פעילים</span>
              <button 
                type="button"
                onClick={() => {
                  setShowInactive(false);
                  if (statusFilter === 'inactive') setStatusFilter('all');
                }}
                className="text-red-700 hover:text-red-900 underline font-bold cursor-pointer mr-1"
                title="בטל סימון V להסתרת אנשי צוות לא פעילים"
              >
                הסתר
              </button>
            </span>
          )}
        </div>
        {(searchTerm || roleFilter !== 'all' || substituteFilter !== 'all' || statusFilter !== 'all' || showDuplicatesOnly || showInactive) && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setRoleFilter('all');
              setSubstituteFilter('all');
              setStatusFilter('all');
              setShowDuplicatesOnly(false);
              setShowInactive(false);
            }}
            className="text-indigo-600 hover:text-indigo-800 hover:underline font-bold text-xs cursor-pointer flex items-center gap-1"
          >
            <RotateCcw size={12} />
            <span>איפוס כל המסננים</span>
          </button>
        )}
      </div>

      <AnimatePresence>
        {(isAdding || editingId) && (
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white p-8 rounded-3xl border border-indigo-100 shadow-xl shadow-indigo-50/50"
          >
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-indigo-900">
              <UserPlus size={24} />
              {editingId ? 'עריכת פרטי איש צוות' : 'הוספת איש צוות חדש'}
            </h3>
            <form onSubmit={editingId ? (e) => { e.preventDefault(); handleUpdateStaff(editingId); } : handleAddStaff} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם פרטי</label>
                <div className="relative">
                  <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם פרטי"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם משפחה</label>
                <div className="relative">
                  <UserCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם משפחה"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">תעודת זהות</label>
                <div className="relative">
                  <IdCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    required
                    value={formData.nationalId}
                    onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-mono"
                    placeholder="9 ספרות"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <BirthDatePicker
                  label="תאריך לידה"
                  value={formData.birthDate}
                  onChange={(value) => setFormData({ ...formData, birthDate: value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">טלפון נייד</label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="05X-XXXXXXX"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">תפקיד / התמחות</label>
                <div className="relative">
                  <Briefcase className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <select
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      let newType = formData.type;
                      
                      // Auto-switch type based on role
                      if (newRole.includes('סייע') || newRole.includes('אם בית')) {
                        newType = 'assistant';
                      } else if (newRole && newRole !== 'אחר') {
                        newType = 'educational';
                      }
                      
                      setFormData({ ...formData, role: newRole, type: newType });
                    }}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                  >
                    <option value="">בחר תפקיד...</option>
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                    <option value="אחר">אחר (הזן ידנית בהגדרות)</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שיוך לקבוצה</label>
                <div className="relative">
                  <Users className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                  >
                    <option value="educational">צוות חינוכי טיפולי</option>
                    <option value="assistant">סייעות / אם בית</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">שם קרוב משפחה</label>
                <div className="relative">
                  <Users className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={formData.relativeName}
                    onChange={(e) => setFormData({ ...formData, relativeName: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="שם קרוב משפחה"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 mr-2">טלפון קרוב משפחה</label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="tel"
                    value={formData.relativePhone}
                    onChange={(e) => setFormData({ ...formData, relativePhone: e.target.value })}
                    className="w-full pr-10 pl-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    placeholder="05X-XXXXXXX"
                  />
                </div>
              </div>

              <div className="md:col-span-2 lg:col-span-3 space-y-6 p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={20} className="text-indigo-600" />
                  <h4 className="font-bold text-slate-800">מערכת שעות שבועית</h4>
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {daysOfWeek.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        setActiveScheduleDay(day);
                        // If no hours are selected for this day, set default 1-8
                        if (!formData.schedule?.[day] || formData.schedule[day].length === 0) {
                          setFormData({
                            ...formData,
                            schedule: {
                              ...formData.schedule,
                              [day]: ['1', '2', '3', '4', '5', '6', '7', '8']
                            }
                          });
                        }
                      }}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                        activeScheduleDay === day
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-white text-slate-500 border border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-slate-400 font-medium">בחר שעות עבודה עבור יום {activeScheduleDay}:</p>
                    {(formData.schedule?.[activeScheduleDay]?.length || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            schedule: {
                              ...formData.schedule,
                              [activeScheduleDay]: []
                            }
                          });
                        }}
                        className="text-xs font-bold text-red-500 hover:text-red-700 transition-colors flex items-center gap-1"
                      >
                        <X size={14} />
                        נקה יום זה
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {schoolHours.map(hour => {
                      const isSelected = formData.schedule?.[activeScheduleDay]?.includes(hour);
                      return (
                        <button
                          key={hour}
                          type="button"
                          onClick={() => {
                            const currentDayHours = formData.schedule?.[activeScheduleDay] || [];
                            const newDayHours = isSelected
                              ? currentDayHours.filter(h => h !== hour)
                              : [...currentDayHours, hour];
                            setFormData({
                              ...formData,
                              schedule: {
                                ...formData.schedule,
                                [activeScheduleDay]: newDayHours
                              }
                            });
                          }}
                          className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'bg-slate-50 text-slate-500 border border-slate-200 hover:border-indigo-300'
                          }`}
                        >
                          {hour}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <input
                  type="checkbox"
                  id="isSubstitute"
                  checked={formData.isSubstitute}
                  onChange={(e) => setFormData({ ...formData, isSubstitute: e.target.checked })}
                  className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="isSubstitute" className="font-bold text-slate-700 cursor-pointer">
                  מילוי מקום
                </label>
              </div>

              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="isActive" className="font-bold text-slate-700 cursor-pointer">
                  איש צוות פעיל
                </label>
              </div>

              <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => { setIsAdding(false); setEditingId(null); }}
                  className="px-8 py-3 rounded-xl font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-indigo-600 text-white px-10 py-3 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                  <span>{editingId ? 'עדכון פרטים' : 'שמירת איש צוות'}</span>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            className="bg-indigo-900 text-white p-3.5 rounded-2xl shadow-xl flex flex-wrap items-center justify-between gap-3 border border-indigo-700/50"
          >
            <div className="flex items-center gap-2">
              <span className="bg-indigo-800 text-indigo-200 text-xs px-2.5 py-1 rounded-full font-bold">
                {selectedIds.length} נבחרו
              </span>
              <span className="text-xs text-indigo-100 font-medium">פעולות מרוכזות על אנשי צוות מסומנים:</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleBulkStatusChange(true)}
                disabled={actionLoading}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                title="הגדר אנשי צוות מסומנים כפעילים"
              >
                <UserCheck size={15} />
                <span>הגדר כפעילים</span>
              </button>
              <button
                onClick={() => handleBulkStatusChange(false)}
                disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                title="הגדר אנשי צוות מסומנים כלא פעילים (סימון לא פעיל)"
              >
                <UserX size={15} />
                <span>הגדר כלא פעילים</span>
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={actionLoading}
                className="bg-rose-600 hover:bg-rose-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                title="מחיקת אנשי צוות מסומנים"
              >
                <Trash2 size={15} />
                <span>מחיקה</span>
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
              >
                ביטול בחירה
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4" size={40} />
            <p className="font-medium">טוען רשימת צוות...</p>
          </div>
        ) : filteredStaff.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-6 py-4 w-10">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.length === filteredStaff.length && filteredStaff.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th 
                    onClick={() => handleSort('fullName')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>שם</span>
                      {renderSortIcon('fullName')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('nationalId')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>תעודת זהות</span>
                      {renderSortIcon('nationalId')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('role')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>תפקיד</span>
                      {renderSortIcon('role')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('active')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>סטטוס</span>
                      {renderSortIcon('active')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('phone')}
                    className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>פרטי קשר</span>
                      {renderSortIcon('phone')}
                    </div>
                  </th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">מערכת שעות</th>
                  {category === 'assistant' && (
                    <th 
                      onClick={() => handleSort('type')}
                      className="px-6 py-4 font-bold text-slate-700 text-sm cursor-pointer hover:bg-slate-100 transition-colors select-none group"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>סוג</span>
                        {renderSortIcon('type')}
                      </div>
                    </th>
                  )}
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm text-center">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredStaff.map((s) => (
                  <tr 
                    key={s.id} 
                    className={`hover:bg-slate-50/50 transition-colors ${
                      selectedIds.includes(s.id) ? 'bg-indigo-50/30' : ''
                    } ${potentialDuplicates.has(s.id) ? 'bg-amber-50/40' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.includes(s.id)}
                        onChange={() => toggleSelectOne(s.id)}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => setViewingStaff(s)}
                        className="flex flex-col text-right hover:text-indigo-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 group-hover:text-indigo-600">{s.firstName} {s.lastName}</span>
                          {potentialDuplicates.has(s.id) && (
                            <span className="text-amber-500" title="כפילות פוטנציאלית">
                              <AlertTriangle size={14} />
                            </span>
                          )}
                        </div>
                        {s.birthDate && <span className="text-[10px] text-slate-400">ת"ל: {s.birthDate}</span>}
                      </button>
                    </td>
                    <td className="px-6 py-4 font-mono text-slate-500 text-sm">{s.nationalId}</td>
                    <td className="px-6 py-4 text-slate-500 text-sm">{s.role || '-'}</td>
                    <td className="px-6 py-4">
                      <select
                        value={s.active !== false ? 'active' : 'inactive'}
                        onChange={(e) => handleStatusChange(s, e.target.value as 'active' | 'inactive')}
                        disabled={actionLoading}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1.5 outline-none border transition-all cursor-pointer ${
                          s.active === false
                            ? 'bg-red-50 text-red-700 border-red-200 focus:ring-2 focus:ring-red-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200 focus:ring-2 focus:ring-emerald-200'
                        }`}
                        title="שנה סטטוס פעיל / לא פעיל"
                      >
                        <option value="active" className="bg-white text-emerald-800 font-bold">פעיל</option>
                        <option value="inactive" className="bg-white text-red-800 font-bold">לא פעיל</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-[11px]">
                        {s.phone && (
                          <a 
                            href={`https://wa.me/${s.phone.replace(/^0/, '972')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-bold transition-colors"
                            title="שלח הודעת וואטסאפ"
                          >
                            <Phone size={10} />
                            <span>{s.phone}</span>
                          </a>
                        )}
                        {s.relativeName && (
                          <div className="flex items-center gap-1 text-slate-400">
                            <Users size={10} />
                            <span>{s.relativeName}: {s.relativePhone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {daysOfWeek.map(day => {
                          const hours = s.schedule?.[day];
                          if (!hours || hours.length === 0) return null;
                          return (
                            <div key={day} className="flex items-center gap-2 text-[10px]">
                              <span className="font-bold text-slate-700 w-8">{day.substring(0, 1)}:</span>
                              <div className="flex flex-wrap gap-0.5">
                                {hours.map(h => (
                                  <span key={h} className="w-3.5 h-3.5 flex items-center justify-center bg-indigo-50 text-indigo-600 rounded-[2px] font-bold">
                                    {h}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {(!s.schedule || (Object.values(s.schedule) as string[][]).every(h => h.length === 0)) && <span className="text-slate-400">-</span>}
                      </div>
                    </td>
                    {category === 'assistant' && (
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                          s.isSubstitute 
                            ? 'bg-amber-50 text-amber-700' 
                            : 'bg-indigo-50 text-indigo-700'
                        }`}>
                          {s.isSubstitute ? 'מילוי מקום' : 'קבוע'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleStatusChange(s, s.active === false ? 'active' : 'inactive')}
                          disabled={actionLoading}
                          className={`p-2 rounded-xl transition-all ${
                            s.active === false
                              ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                              : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title={s.active === false ? 'החזר לפעיל' : 'סמן כלא פעיל'}
                        >
                          {s.active === false ? <UserCheck size={18} /> : <UserX size={18} />}
                        </button>
                        <button
                          onClick={async () => {
                            const newType = s.type === 'educational' ? 'assistant' : 'educational';
                            const confirmMsg = `האם להעביר את ${s.fullName} לרשימת ${newType === 'educational' ? 'צוות חינוכי' : 'סייעות'}?`;
                            if (window.confirm(confirmMsg)) {
                              setActionLoading(true);
                              try {
                                await updateDoc(doc(db, 'staff', s.id), { type: newType });
                                dataCache.remove('staff');
                                fetchStaff(true);
                              } catch (e) {
                                handleFirestoreError(e, OperationType.UPDATE, 'staff');
                              } finally {
                                setActionLoading(false);
                              }
                            }
                          }}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                          title="העבר קבוצה"
                        >
                          <RefreshCw size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(s.id);
                            setIsAdding(false);
                            setFormData({
                              firstName: s.firstName || '',
                              lastName: s.lastName || '',
                              nationalId: s.nationalId,
                              birthDate: s.birthDate || '',
                              phone: s.phone || '',
                              relativeName: s.relativeName || '',
                              relativePhone: s.relativePhone || '',
                              role: s.role || '',
                              type: s.type || category,
                              isSubstitute: s.isSubstitute || false,
                              active: s.active !== false,
                              schedule: s.schedule || {}
                            });
                            
                            // Scroll to form
                            setTimeout(() => {
                              formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                          title="עריכה"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(s.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                          title="מחיקה"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 flex flex-col items-center justify-center text-slate-400">
            <div className="p-5 bg-slate-50 rounded-full mb-4">
              <UserCog size={40} />
            </div>
            <p className="font-medium text-lg text-slate-600">לא נמצאו אנשי צוות התואמים לחיפוש או לסינון</p>
            {!showInactive && inactiveStaff.length > 0 && (
              <button
                type="button"
                onClick={() => setShowInactive(true)}
                className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-bold bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-xl transition-all flex items-center gap-1.5"
              >
                <UserX size={16} />
                <span>הצג {inactiveStaff.length} אנשי צוות לא פעילים</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
